import React, {FormEvent, useMemo} from "react";
import {CheckboxItem, Icon, styled, theme} from "@reside/ui";
import {TableUserDto} from "../../../../utils/types/user";

import {SORT_DIRECTION} from "../../../../utils/constants";
import {Column, ListTable} from "../../../../atoms/list-table";
import {TextCellEllipsis} from "../../../../atoms/list-table/SharedCells";
import {readableRole} from "../../../../atoms/list-table/helpers";
import {getPermissionsManagementPageSelectionState} from "../permissions-management-filters/permissionsManagementSelection.helpers";
import "./ListTableUserPermissions.scss";

type Row = {
  rowData: TableUserDto;
  rowIndex: number;
};

type Props = {
  height?: number;
  users: ReadonlyArray<TableUserDto>;
  selectedUserIds?: ReadonlyArray<string>;
  onSelectedUserIdsChange?: (userIds: ReadonlyArray<string>) => void;
  onRowClick?: (id: string, row: Row) => void;
  sortPredicates?: Record<string, string[]>;
  onSort: (sortBy: string[], direction: "ASC" | "DESC") => void;
};

export const ListTableUserPermissions = ({
  users,
  height,
  selectedUserIds = [],
  onSelectedUserIdsChange,
  onRowClick,
  sortPredicates,
  onSort,
}: Props) => {
  const isRowSelectionEnabled = Boolean(onSelectedUserIdsChange);
  const pageUserIds = useMemo(
    () =>
      users
        .map(user => user.id)
        .filter((userId): userId is string => Boolean(userId)),
    [users],
  );

  const {allSelected, someSelected} =
    getPermissionsManagementPageSelectionState(selectedUserIds, pageUserIds);

  const toggleUserSelection = (userId: string) => {
    if (!onSelectedUserIdsChange) {
      return;
    }

    onSelectedUserIdsChange(
      selectedUserIds.includes(userId)
        ? selectedUserIds.filter(id => id !== userId)
        : [...selectedUserIds, userId],
    );
  };

  const togglePageSelection = () => {
    if (!onSelectedUserIdsChange) {
      return;
    }

    if (allSelected) {
      const pageUserIdSet = new Set(pageUserIds);
      onSelectedUserIdsChange(
        selectedUserIds.filter(userId => !pageUserIdSet.has(userId)),
      );

      return;
    }

    onSelectedUserIdsChange(
      Array.from(new Set([...selectedUserIds, ...pageUserIds])),
    );
  };

  const table = (
    <ListTable
      basic
      widthPercent
      sortedData={users}
      height={height}
      getRowId={(row: Row) => row.rowData.id}
      rowHeight={120}
      noRowsMessage="No Users found with these parameters..."
      sortPredicates={sortPredicates}
      sortBy="user"
      sortDirection="ASC"
      showDoubleArrowOnNotSorted
      nextSortDirection={{
        [SORT_DIRECTION.ASC]: SORT_DIRECTION.DESC,
        [SORT_DIRECTION.DESC]: SORT_DIRECTION.ASC,
      }}
      indicatorName={{
        [SORT_DIRECTION.ASC]: "sort-up",
        [SORT_DIRECTION.DESC]: "sort-down",
      }}
      onRowClick={onRowClick}
      onSort={(
        sortBy: keyof typeof sortPredicates,
        direction: "ASC" | "DESC",
      ) => onSort(sortPredicates[sortBy], direction)}
    >
      {isRowSelectionEnabled && (
        <Column
          headerCellRenderer={() => (
            <TableSelectionCheckbox
              checked={allSelected || someSelected}
              someSelected={someSelected}
              onChange={togglePageSelection}
            />
          )}
          cellRenderer={({rowData}: Row) => {
            const userId = rowData.id;

            if (!userId) {
              return null;
            }

            return (
              <TableSelectionCheckbox
                checked={selectedUserIds.includes(userId)}
                onChange={() => toggleUserSelection(userId)}
              />
            );
          }}
          label=""
          stopRowClick
          width={1}
        />
      )}
      <Column
        cellRenderer={({rowData: {firstName, lastName, email}}: Row) => (
          <UserCell>
            <UserName>
              {firstName} {lastName}
            </UserName>
            {email && <UserEmail>{email}</UserEmail>}
          </UserCell>
        )}
        label="User"
        sortKey="user"
        width={5}
      />
      <Column
        label="Role"
        sortKey="role"
        width={5}
        cellRenderer={(row: Row) => (
          <TextCellEllipsis>
            {readableRole(row.rowData.role as any)}
          </TextCellEllipsis>
        )}
      />
      <Column
        label="Permissions"
        sortKey="permission"
        width={5}
        cellRenderer={(row: Row) => {
          const count = row.rowData.permissions?.length ?? 0;

          return (
            <TextCellEllipsis>
              {count === 0 ? (
                <span style={{color: theme.color.gray100}}>
                  — No Permissions
                </span>
              ) : (
                `${count} permission${count === 1 ? "" : "s"}`
              )}
            </TextCellEllipsis>
          );
        }}
      />
      <Column
        label="Organization"
        sortKey="organization"
        width={10}
        cellRenderer={(row: Row) => (
          <TextCellEllipsis>{row.rowData.organization?.name}</TextCellEllipsis>
        )}
      />
      <Column
        label="Facility"
        width={10}
        cellRenderer={(row: Row) => (
          <TextCellEllipsis>
            {row.rowData.facilities?.map(facility => facility.name).join(", ")}
          </TextCellEllipsis>
        )}
      />
    </ListTable>
  );

  if (!isRowSelectionEnabled) {
    return table;
  }

  return <SelectionTableWrapper>{table}</SelectionTableWrapper>;
};

ListTableUserPermissions.defaultProps = {
  sortPredicates: {
    user: ["firstName", "lastName"],
    role: ["role"],
  },
};

type TableSelectionCheckboxProps = Readonly<{
  checked: boolean;
  someSelected?: boolean;
  onChange: () => void;
}>;

const TableSelectionCheckbox = ({
  checked,
  someSelected = false,
  onChange,
}: TableSelectionCheckboxProps) => (
  <CheckboxCell>
    <CheckboxItem
      checked={checked}
      label=""
      Icon={props =>
        checked && someSelected ? (
          <Icon {...props} name="minus-1" />
        ) : (
          <Icon {...props} />
        )
      }
      onChange={(event: FormEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onChange();
      }}
    />
  </CheckboxCell>
);

const CheckboxCell = styled.div`
  display: flex;
  font-size: 16px;
  justify-content: center;

  label {
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 0;
    padding: 0;
  }

  label > span {
    display: none;
    margin: 0;
    padding: 0;
  }
`;

const SelectionTableWrapper = styled.div`
  table.list-table-basic tr th:first-child,
  table.list-table-basic tr td:first-child {
    min-width: 40px;
    padding-left: 0;
    padding-right: 0;
    text-align: center;
    vertical-align: middle;
    width: 40px;
  }

  table.list-table-basic tr th:first-child {
    font-weight: normal;
    letter-spacing: normal;
    padding-bottom: 12px;
    padding-top: 12px;
  }
`;

const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const UserName = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.span`
  color: ${theme.color.gray100};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 8px;
  white-space: nowrap;
`;
